import { useEffect, useMemo, useState } from 'react';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
  PortalDsNoticeBar,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import type {
  DataJobDefinition,
  DataJobParameterField,
  DataJobRunFormMode,
} from '../types/data-jobs.types';

export type DataJobRunFormValuesV2 = {
  job_key: string;
  trigger_type: 'manual' | 'backfill';
  parameters: Record<string, unknown>;
};

interface DataJobRunFormV2Props {
  definitions: DataJobDefinition[];
  mode: DataJobRunFormMode;
  isSaving?: boolean;
  submitError?: string | null;
  onSubmit: (values: DataJobRunFormValuesV2) => void;
  onCancel?: () => void;
}

function getInitialParameters(fields: DataJobParameterField[]): Record<string, unknown> {
  const parameters: Record<string, unknown> = {};
  fields.forEach((field) => {
    if (field.type === 'multi_select') {
      parameters[field.name] = [];
    } else {
      parameters[field.name] = '';
    }
  });
  return parameters;
}

function MultiSelectFieldV2({
  field,
  value,
  onChange,
}: {
  field: DataJobParameterField;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const selected = new Set(value);

  return (
    <div className="space-y-2 rounded-lg border border-[#EDEEF2] bg-white p-3">
      {(field.options ?? []).map((option) => {
        const checked = selected.has(option.value);
        return (
          <label key={option.value} className="flex items-center gap-2 text-[13.5px] text-[#0D0F1A]">
            <input
              type="checkbox"
              className="size-4 accent-[#2563EB]"
              checked={checked}
              onChange={() => {
                const next = new Set(selected);
                if (checked) {
                  next.delete(option.value);
                } else {
                  next.add(option.value);
                }
                onChange([...next]);
              }}
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export function DataJobRunFormV2({
  definitions,
  mode,
  isSaving = false,
  submitError = null,
  onSubmit,
  onCancel,
}: DataJobRunFormV2Props) {
  const availableDefinitions = useMemo(
    () => definitions.filter((definition) => {
      if (mode === 'backfill') {
        return definition.supports_backfill;
      }
      return definition.supports_manual_run;
    }),
    [definitions, mode],
  );

  const [jobKey, setJobKey] = useState(availableDefinitions[0]?.key ?? '');
  const selectedDefinition = availableDefinitions.find((definition) => definition.key === jobKey);
  const fields = selectedDefinition?.parameter_fields ?? [];

  const [parameters, setParameters] = useState<Record<string, unknown>>(
    getInitialParameters(fields),
  );

  useEffect(() => {
    setParameters(getInitialParameters(selectedDefinition?.parameter_fields ?? []));
  }, [jobKey, selectedDefinition?.parameter_fields]);

  const validationError = useMemo(() => {
    if (!selectedDefinition) {
      return 'Select a job.';
    }
    if (!selectedDefinition.enabled) {
      return `${selectedDefinition.label} is disabled. Enable the job feature flag before running.`;
    }

    for (const field of fields) {
      const value = parameters[field.name];
      if (field.type === 'date' && field.required && !value) {
        return `${field.label} is required.`;
      }
      if (field.type === 'multi_select' && field.required) {
        const selected = Array.isArray(value) ? value : [];
        if (!selected.length) {
          return `${field.label} is required.`;
        }
      }
    }

    const startDate = String(parameters.start_date ?? '');
    const endDate = String(parameters.end_date ?? '');
    if (startDate && endDate && endDate < startDate) {
      return 'End date must be on or after start date.';
    }

    return null;
  }, [fields, parameters, selectedDefinition]);

  const canSubmit = Boolean(selectedDefinition?.enabled) && !validationError && !isSaving;

  const handleSubmit = () => {
    if (!canSubmit || !selectedDefinition) {
      return;
    }

    const normalised: Record<string, unknown> = {};
    fields.forEach((field) => {
      const value = parameters[field.name];
      if (field.type === 'multi_select') {
        const selected = Array.isArray(value) ? value.filter(Boolean) : [];
        if (selected.length) {
          normalised[field.name] = selected;
        }
        return;
      }
      if (value) {
        normalised[field.name] = value;
      }
    });

    onSubmit({
      job_key: selectedDefinition.key,
      trigger_type: mode === 'backfill' ? 'backfill' : 'manual',
      parameters: normalised,
    });
  };

  if (!availableDefinitions.length) {
    return (
      <p className={portalDs.bodySm}>
        No jobs are registered for this run type yet.
      </p>
    );
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <PortalDsField label="Job" htmlFor="etl-run-job-key" hint={selectedDefinition?.description}>
        <PortalDsSelect
          id="etl-run-job-key"
          value={jobKey}
          onChange={(event) => setJobKey(event.target.value)}
          disabled={isSaving}
        >
          {availableDefinitions.map((definition) => (
            <option key={definition.key} value={definition.key} disabled={!definition.enabled}>
              {definition.label}
              {!definition.enabled ? ' (disabled)' : ''}
            </option>
          ))}
        </PortalDsSelect>
      </PortalDsField>

      {fields.map((field) => (
        <PortalDsField
          key={field.name}
          label={field.label}
          htmlFor={`etl-run-${field.name}`}
          required={field.required}
          hint={field.help_text}
        >
          {field.type === 'date' ? (
            <PortalDsInput
              id={`etl-run-${field.name}`}
              type="date"
              value={String(parameters[field.name] ?? '')}
              onChange={(event) => setParameters((current) => ({
                ...current,
                [field.name]: event.target.value,
              }))}
              disabled={isSaving}
            />
          ) : (
            <MultiSelectFieldV2
              field={field}
              value={Array.isArray(parameters[field.name]) ? parameters[field.name] as string[] : []}
              onChange={(next) => setParameters((current) => ({
                ...current,
                [field.name]: next,
              }))}
            />
          )}
        </PortalDsField>
      ))}

      {validationError ? (
        <PortalDsNoticeBar>
          {validationError}
        </PortalDsNoticeBar>
      ) : null}

      {submitError ? (
        <PortalDsAlert variant="danger" title="Could not start run">
          {submitError}
        </PortalDsAlert>
      ) : null}

      <div className="flex items-center gap-3 pt-2">
        <PortalDsButton type="submit" variant="accent" disabled={!canSubmit}>
          {isSaving ? 'Starting run…' : 'Start run'}
        </PortalDsButton>
        {onCancel ? (
          <PortalDsButton type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </PortalDsButton>
        ) : null}
      </div>
    </form>
  );
}
